import React, { useState, useCallback, useEffect, useRef } from "react";
import { useSelector, useDispatch } from "react-redux";
import withRouter from "utils/withRouter";
import { hasRoleIn } from 'rbac/RbacHelper';
import PERMISSIONS from "rbac/Permissions.json";
import {
  updateInterventionList,
  updateIndexOnList,
  resetError,
} from "./HscSwitchList.actions";
import { 
  updateCloseReason,
  UPDATE_CLOSE_REASON_SUCCESS,
  UPDATE_CLOSE_REASON_FAILURE 
} from "components/CloseReasonDropdown/CloseReasonDropdown.actions";
import ResetListState from "components/ResetListState";

import Container from "react-bootstrap/Container";
import Row from "react-bootstrap/Row";
import Col from "react-bootstrap/Col";

import { DEFAULT_HSC_SWIM_SORT, DEFAULT_HSC_SWIM_FILTER } from "lookup/SwimConstants";
import HscSwitchListCommon from "./HscSwitchListCommon";
import { COLUMN } from "./CreateColumnListForHscSwitchList";

const DEFAULT_PAGE_SIZE = 15;

const HscSwitchList = ({ history }) => {
  const dispatch = useDispatch();
  const isInitialMount = useRef(true);

  const { 
    data, 
    pages, 
    totalElements, 
    loading, 
    listState, 
    isError 
  } = useSelector((state) => {
    let listState = state.hscSwitchList.listState;
    if (!listState) {
      listState = {
        page: 0,
        pageSize: DEFAULT_PAGE_SIZE,
        sorted: DEFAULT_HSC_SWIM_SORT,
        filtered:
          [...DEFAULT_HSC_SWIM_FILTER, { id: "priorityAgent", value: false }],
        resized: [],
        expanded: {},
      };
    }
    return {
      data: state.hscSwitchList.data !== undefined ? state.hscSwitchList.data : [],
      pages: state.hscSwitchList.pages ? state.hscSwitchList.pages : 0,
      totalElements:
        state.hscSwitchList.totalElements !== undefined
          ? state.hscSwitchList.totalElements
          : "Loading ...",
      loading: state.hscSwitchList.loading,
      listState: listState,
      isError: state.hscSwitchList.isError,
    };
  });

  const canViewPhi = hasRoleIn([PERMISSIONS.PERM_PHI_ACCESS]);

  const COLUMN_LIST = [
    COLUMN.customerNameAbbreviation,
    COLUMN.clientNameAbbreviation,
    COLUMN.switchNumber,
    COLUMN.prescriberName,
    COLUMN.prescriberPhone,
    COLUMN.prescriberFax,
    COLUMN.prescriberNPI,
  ];

  if (canViewPhi) {
    COLUMN_LIST.push(COLUMN.memberPhi);
  }

  COLUMN_LIST.push(COLUMN.closeReason);
  COLUMN_LIST.push(COLUMN.notes);
  COLUMN_LIST.push(COLUMN.processedDate);

  const updateData = useCallback((listOptions) => {
    dispatch(updateInterventionList(
      listOptions.pageSize,
      listOptions.page,
      listOptions.sorted,
      listOptions.filtered,
      listOptions.resized,
      listOptions.expanded
    ));
  }, [dispatch]);

  useEffect(() => {
    if (isInitialMount.current) {
      isInitialMount.current = false;
      updateData(listState);
    }
  });

  const resetList = useCallback(() => {
    const { pageSize, resized, expanded } = listState;
    dispatch(updateInterventionList(
      pageSize,
      0,
      DEFAULT_HSC_SWIM_SORT,
      [...DEFAULT_HSC_SWIM_FILTER, { id: "priorityAgent", value: false }],
      resized,
      expanded
    ));
  }, [dispatch, listState]);

  const onUpdateIndexOnList = useCallback((index) => {
    dispatch(updateIndexOnList(index));
  }, [dispatch]);

  const onResetError = useCallback(() => {
    dispatch(resetError());
  }, [dispatch]);

  const onNoteUpdate = useCallback(() => {
    updateData(listState);
  }, [updateData, listState]);

  const onConfirm = useCallback((reason, wfItemId, globalSwitchId, resetLoading) => {
    dispatch(updateCloseReason(wfItemId, reason))
    .then((action) => {
      if (action.type === UPDATE_CLOSE_REASON_SUCCESS) {
        return dispatch(updateInterventionList(listState.pageSize, listState.page, listState.sorted, listState.filtered, listState.resized, listState.expanded));
      }
    })
    .finally(() => {
      resetLoading();
    });
  }, [dispatch, listState]);


  return (
    <Container className="list-container" fluid>
      <Row>
        <Col md={12}>
          <div
            className="codex-version-display-bar"
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <span style={{ float: "left" }}>
              Total Switches: <strong>{totalElements}</strong>
            </span>
            <strong style={{ textAlign: "center" }}>HSC Switch List</strong>
            <div
              style={{
                display: "flex",
                alignItems: "center",
                float: "right",
              }}
            >
            <ResetListState onReset={resetList} />
            </div>
          </div>
        </Col>
      </Row>
      <HscSwitchListCommon
        updateData={updateData}
        updateIndexOnList={onUpdateIndexOnList}
        resetError={onResetError}
        data={data}
        pages={pages}
        totalElements={totalElements}
        loading={loading}
        listState={listState}
        isError={isError}
        columnList={COLUMN_LIST}
        isSortFilterEnabledForCust={false}
        onNoteUpdate={onNoteUpdate}
        onConfirm={onConfirm}
      />
    </Container>
  );
};

export default withRouter(HscSwitchList);
